/**
 * Timezone-safe date helpers for "YYYY-MM-DD" strings stored in the DB.
 *
 * Problem: `new Date('2026-07-10')` parses as UTC midnight. In any negative-
 * offset timezone (e.g. America/Chicago UTC-5) this displays as "July 9".
 * That caused the ASNA training (stored as `2026-07-10`) to show as
 * "Friday July 9 – Sunday July 11" on Central-time browsers.
 *
 * Fix: split the YYYY-MM-DD string and construct a Date with explicit local
 * components — anchored at local noon for extra safety against DST edges.
 *
 * All helpers gracefully return null/empty for falsy input so they never
 * crash in legacy mobile WebViews.
 */

/** Convert a "YYYY-MM-DD" string into a local-time Date at noon. */
export function parseYMD(value) {
  if (!value || typeof value !== "string") return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), 12, 0, 0);
}

/** Locale-format a "YYYY-MM-DD" string. Returns "" if the input is unparseable. */
export function formatYMD(value, options = { month: "long", day: "numeric", year: "numeric" }, locale = "en-US") {
  const d = parseYMD(value);
  return d ? d.toLocaleDateString(locale, options) : "";
}

/** Format a YYYY-MM-DD range: "Jul 10 – 12, 2026" (or single date if no end / equal). */
export function formatYMDRange(start, end, { showYear = true } = {}) {
  const s = parseYMD(start);
  if (!s) return "";
  const e = parseYMD(end);
  const monthDay = { month: "short", day: "numeric" };
  const monthDayYear = { ...monthDay, year: "numeric" };
  if (!e || end === start) return s.toLocaleDateString("en-US", showYear ? monthDayYear : monthDay);
  // Same month → "Jul 10 – 12, 2026"
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${s.toLocaleDateString("en-US", monthDay)} – ${e.getDate()}${showYear ? `, ${e.getFullYear()}` : ""}`;
  }
  // Cross-month → "Jul 30 – Aug 3, 2026"
  return `${s.toLocaleDateString("en-US", monthDay)} – ${e.toLocaleDateString("en-US", showYear ? monthDayYear : monthDay)}`;
}

/**
 * Format a 24-hour "HH:MM" string as "1 pm" / "9:30 am".
 * Returns "" for falsy or invalid input so callers can safely concat.
 */
export function formatTime(value) {
  if (!value || typeof value !== "string") return "";
  const m = value.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  let h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return "";
  const period = h >= 12 ? "pm" : "am";
  h = h % 12;
  if (h === 0) h = 12;
  return min === 0 ? `${h} ${period}` : `${h}:${String(min).padStart(2, "0")} ${period}`;
}

/**
 * Format a start/end time pair as a compact range:
 *   ("13:00", "19:00") → "1–7 pm"
 *   ("09:00", "17:00") → "9 am – 5 pm"
 *   ("09:30", "17:00") → "9:30 am – 5 pm"
 *   ("13:00", null)    → "1 pm"
 */
export function formatTimeRange(start, end) {
  const s = formatTime(start);
  const e = formatTime(end);
  if (!s && !e) return "";
  if (!e) return s;
  if (!s) return e;
  // Collapse the am/pm on the start when both share it AND minutes are :00
  const sMatch = s.match(/^(\d{1,2}(?::\d{2})?)\s(am|pm)$/);
  const eMatch = e.match(/^(\d{1,2}(?::\d{2})?)\s(am|pm)$/);
  if (sMatch && eMatch && sMatch[2] === eMatch[2] && !sMatch[1].includes(":")) {
    return `${sMatch[1]}\u2013${eMatch[1]} ${eMatch[2]}`;
  }
  return `${s} \u2013 ${e}`;
}
